import { useAuthPages, authPageFailureText, type AuthPageFailure, registrationFailure } from "../i18n/authPagesPresentation";
import { Fragment, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Input } from "../components/ui/Input";
import { ThemeSwitcher } from "../components/theme/ThemeSwitcher";
import { Alert } from "../components/ui/Alert";
import { useAuth } from "../components/auth/AuthProvider";
import { AuthBrandingHeader } from "../components/auth/AuthBrandingHeader";
import { toast } from "../lib/toast";
import { usePublicConfig } from "../hooks/usePublicConfig";
import { useBrandingSettings } from "../lib/settings/useBrandingSettings";

type ActivationState = null | "email" | "admin";

export const RegisterPage = () => {
  const t = useAuthPages();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const { data: publicConfig, loading: publicConfigLoading } = usePublicConfig();
  const branding = useBrandingSettings();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AuthPageFailure | null>(null);
  const [activationPending, setActivationPending] = useState<ActivationState>(null);
  const registrationEnabled = publicConfig?.registrationEnabled ?? true;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!registrationEnabled) {
      const failure: AuthPageFailure = { key: "registrationDisabled" };
      setError(failure);
      toast.error(authPageFailureText(failure, t)!);
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const res = await fetch("/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, email, password })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const failure = registrationFailure(data?.error?.code, data?.error?.message ?? data?.message, data?.presentation);
        setError(failure);
        toast.error(authPageFailureText(failure, t)!);
        return;
      }

      // Handle activation modes
      if (data.activation === "email") {
        setActivationPending("email");
        toast.success(t($ => $.createdEmail));
        return;
      }

      if (data.activation === "admin") {
        setActivationPending("admin");
        toast.success(t($ => $.createdAdmin));
        return;
      }

      // Mode 1: instant activation — session established
      toast.success(t($ => $.createdWelcome));
      await refresh();
      navigate("/profile", { replace: true });
    } catch {
      const failure: AuthPageFailure = { key: "network" };
      setError(failure);
      toast.error(authPageFailureText(failure, t)!);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="surface-grid flex min-h-screen items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <AuthBrandingHeader
          title={t($ => $.registerTitle)}
          subtitle={t($ => $.registerSubtitle)}
          authPages={publicConfig?.authPages}
          branding={branding.settings}
        />

        <Card className="glass-card">
          <div className="mb-5 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">{t($ => $.registerHeading)}</h2>
              <p className="text-sm text-muted-foreground">{t($ => $.registerDescription)}</p>
            </div>
            <ThemeSwitcher compact />
          </div>

          {!registrationEnabled && !publicConfigLoading ? (
            <div className="space-y-4">
              <Alert variant="warning">
                {t($ => $.registrationDisabled)}
              </Alert>
              <Button type="button" size="lg" className="w-full" onClick={() => navigate("/login")}>
                {t($ => $.backToLogin)}
              </Button>
            </div>
          ) : activationPending ? (
            <div className="space-y-4">
              {activationPending === "email" ? (
                <Alert variant="info">
                  <div className="space-y-2">
                    <p className="font-medium">{t($ => $.checkEmail)}</p>
                    <p>{t($ => $.activationEmail, { email: "\uFFFC" }).split("\uFFFC").map((part, index) => (
                      <Fragment key={index}>{index > 0 && <strong>{email}</strong>}{part}</Fragment>
                    ))}</p>
                    <p className="text-xs text-muted-foreground">{t($ => $.activationExpiry)}</p>
                  </div>
                </Alert>
              ) : (
                <Alert variant="info">
                  <div className="space-y-2">
                    <p className="font-medium">{t($ => $.awaitingApproval)}</p>
                    <p>{t($ => $.approvalDescription)}</p>
                  </div>
                </Alert>
              )}
              <Button type="button" size="lg" className="w-full" onClick={() => navigate("/login")}>
                {t($ => $.goToLogin)}
              </Button>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive-text">
                  {authPageFailureText(error, t)}
                </div>
              )}

              <form className="space-y-4" onSubmit={handleSubmit}>
                <div className="space-y-1">
                  <label className="text-xs uppercase tracking-wide text-muted-foreground">{t($ => $.registerUsername)}</label>
                  <Input value={username} onChange={(event) => setUsername(event.target.value)} required />
                </div>
                <div className="space-y-1">
                  <label className="text-xs uppercase tracking-wide text-muted-foreground">{t($ => $.email)}</label>
                  <Input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
                </div>
                <div className="space-y-1">
                  <label className="text-xs uppercase tracking-wide text-muted-foreground">{t($ => $.password)}</label>
                  <Input type="password" value={password} onChange={(event) => setPassword(event.target.value)} required />
                </div>
                <Button type="submit" size="lg" className="w-full" disabled={loading}>
                  {loading ? t($ => $.creating) : t($ => $.createAccount)}
                </Button>
              </form>
              <p className="mt-4 text-center text-xs text-muted-foreground">
                {t($ => $.hasAccount)} <Link to="/login">{t($ => $.signIn)}</Link>
              </p>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};
